import type {
    CreateDmAutomationReq,
    DmAutomationDto,
    TriggerType,
    UpdateDmAutomationReq,
} from '@/types/dmAutomation';
import { parseKeywords, parseTargetPostIds } from '../_shared';

export type WizardStep = 1 | 2 | 3 | 4 | 5;

export interface WizardData {
    name: string;
    triggerType: TriggerType | '';
    keywords: string[];
    target: 'all' | 'specific';
    targetPostIds: string[];
    publicReply: string;
    responseMessage: string;
    buttonText: string;
    buttonLink: string;
    requireFollow: boolean;
    followCheckMessage: string;
    notFollowingMessage: string;
}

export const EMPTY_WIZARD_DATA: WizardData = {
    name: '',
    triggerType: '',
    keywords: [],
    target: 'all',
    targetPostIds: [],
    publicReply: '',
    responseMessage: '',
    buttonText: '',
    buttonLink: '',
    requireFollow: false,
    followCheckMessage: '',
    notFollowingMessage: '',
};

export const STEP_LABELS: Record<WizardStep, string> = {
    1: '트리거',
    2: '이름',
    3: '키워드',
    4: '대상 게시물',
    5: '메시지',
};

export const isStepApplicable = (step: WizardStep, trigger: WizardData['triggerType']): boolean => {
    // 키워드(step 3) 는 모든 트리거에서 입력 가능. STORY_REPLY 는 선택 사항(validateStep 에서 빈 값 허용)
    if (step === 4 && trigger !== 'COMMENT_KEYWORD') return false;
    return true;
};

export const validateStep = (step: WizardStep, data: WizardData): string | null => {
    if (step === 1 && !data.triggerType) return '트리거를 선택해주세요';
    if (step === 2 && !data.name.trim()) return '자동화 이름을 입력해주세요';
    if (step === 3 && data.triggerType !== 'STORY_REPLY' && data.keywords.length === 0) {
        return '키워드를 1개 이상 입력해주세요';
    }
    if (
        step === 4 &&
        data.triggerType === 'COMMENT_KEYWORD' &&
        data.target === 'specific' &&
        data.targetPostIds.length === 0
    ) {
        return '게시물을 1개 이상 선택해주세요';
    }
    if (step === 5 && !data.responseMessage.trim()) return 'DM 본문을 입력해주세요';
    if (step === 5 && data.buttonText.trim() && !data.buttonLink.trim()) return '버튼 링크를 입력해주세요';
    return null;
};

export const buildCreatePayload = (data: WizardData): CreateDmAutomationReq => {
    const trigger = data.triggerType as TriggerType;
    const payload: CreateDmAutomationReq = {
        name: data.name.trim(),
        triggerType: trigger,
        keyword: data.keywords.join(','),
        responseMessage: data.responseMessage,
    };

    if (trigger === 'COMMENT_KEYWORD') {
        payload.targetPost = data.target === 'all' ? 'ALL' : data.targetPostIds.join(',');
        if (data.publicReply.trim()) payload.publicReply = data.publicReply.trim();
    }
    if (data.buttonText.trim()) payload.buttonText = data.buttonText.trim();
    if (data.buttonLink.trim()) payload.buttonLink = data.buttonLink.trim();
    if (data.requireFollow) {
        payload.requireFollow = true;
        if (data.followCheckMessage.trim()) payload.followCheckMessage = data.followCheckMessage.trim();
        if (data.notFollowingMessage.trim()) payload.notFollowingMessage = data.notFollowingMessage.trim();
    }

    return payload;
};

export const buildUpdatePayload = (data: WizardData): UpdateDmAutomationReq => {
    const trigger = data.triggerType as TriggerType;
    const payload: UpdateDmAutomationReq = {
        name: data.name.trim(),
        triggerType: trigger,
        keyword: data.keywords.join(','),
        responseMessage: data.responseMessage,
        requireFollow: data.requireFollow,
        buttonText: data.buttonText.trim(),
        buttonLink: data.buttonLink.trim(),
        followCheckMessage: data.followCheckMessage.trim(),
        notFollowingMessage: data.notFollowingMessage.trim(),
    };

    if (trigger === 'COMMENT_KEYWORD') {
        payload.targetPost = data.target === 'all' ? 'ALL' : data.targetPostIds.join(',');
        payload.publicReply = data.publicReply.trim();
    }

    return payload;
};

export const dtoToWizardData = (dto: DmAutomationDto): WizardData => {
    const targetIds = parseTargetPostIds(dto.targetPost);
    return {
        name: dto.name,
        triggerType: dto.triggerType,
        keywords: parseKeywords(dto.keyword),
        target: dto.targetPost === 'ALL' || targetIds.length === 0 ? 'all' : 'specific',
        targetPostIds: targetIds,
        publicReply: dto.publicReply ?? '',
        responseMessage: dto.responseMessage,
        buttonText: dto.buttonText ?? '',
        buttonLink: dto.buttonLink ?? '',
        requireFollow: dto.requireFollow,
        followCheckMessage: dto.followCheckMessage ?? '',
        notFollowingMessage: dto.notFollowingMessage ?? '',
    };
};
